import { IContainerConfig } from "@dhx/ts-common/core";
import { sv } from "@dhx/ts-common/dom";

const defaultColors = [
	"#2A9D8F",
	"#78586F",
	"#E76F51",
	"#E5A910",
	"#11A3D0",
	"#985F99",
	"#217B70",
	"#BD9391",
	"#9C89B8",
	"#734B5E",
	"#D66BA0",
	"#5C5D8D",
];

const defaultColorsTreeMap = ["#237396", "#2780A8", "#3892A3", "#4DA3A0", "#67BF99"];

export function getDefaultColor(index = 0, isTreeMapRange?: boolean): string {
	return isTreeMapRange ? defaultColorsTreeMap[index] : defaultColors[index];
}

type Locator = (data: any) => number | string;

export function locator(value: string | Locator): Locator {
	if (!value) {
		return () => "";
	}

	if (typeof value === "string") {
		return obj => obj[value];
	} else {
		return value;
	}
}

export function log10(x: number): number {
	return Math.log(x) / Math.LN10;
}

function anyArgsMemo<U>(fn: (...args: string[]) => U): (...args: string[]) => U {
	const cached = {};

	return (...args) => {
		let mem = cached;
		for (let i = 0; i < args.length - 1; i++) {
			mem[args[i]] = mem[args[i]] || {};
			mem = mem[args[i]];
		}
		const last = args.length - 1;
		if (mem[last]) {
			return mem[last];
		}
		return (mem[last] = fn(...args));
	};
}

export const getTextWidth = anyArgsMemo((text: string, font = ""): number => {
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");
	if (font) {
		ctx.font = font;
	}
	return ctx.measureText(text).width;
});

function memo<U>(fn: (arg: string) => U): (arg: string) => U {
	const cached = {};

	return arg => {
		if (cached[arg]) {
			return cached[arg];
		}
		return (cached[arg] = fn(arg));
	};
}

function getRgbaFromColor(color: string): [number, number, number] {
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");
	ctx.fillStyle = color;
	ctx.fillRect(0, 0, 2, 2);
	const rgba = ctx.getImageData(1, 1, 1, 1).data;
	return [rgba[0], rgba[1], rgba[2]];
}

const memoizedColorFromRgba = memo(getRgbaFromColor);

export function getColorShade(color: string, light: number): string {
	const [r, g, b] = memoizedColorFromRgba(color).map(value =>
		Math.floor(value * light + 255 * (1 - light))
	);
	return `rgb(${r},${g},${b})`;
}

export const getFontStyle = memo((className: string): string => {
	const chart: SVGElement = document.createElementNS("http://www.w3.org/2000/svg", "svg");
	chart.setAttribute("class", "dhx_chart");
	const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
	text.setAttribute("class", className);

	chart.setAttribute("visibility", "hidden");
	text.textContent = "test";
	chart.appendChild(text);
	document.body.appendChild(chart);
	const style = getComputedStyle(text);
	const font = `${style.fontSize} ${style.fontFamily}`;
	document.body.removeChild(chart);
	return font;
});

interface IStop {
	offset: number;
	color: string;
	opacity?: number;
}

interface IGradient {
	stops: IStop[];
}

interface IText {
	x: number;
	y: number;
	width: number;
	height: number;
	class: string;
}
interface ITextObj {
	text1: IText;
	text2: IText;
	changeSector: boolean;
	line: number;
	right: boolean;
	dy: number;
}

export function linearGradient(grad: IGradient, id: string) {
	const stops = grad.stops;
	const colors = stops.map(item =>
		sv("stop", {
			offset: `${item.offset * 100}%`,
			"stop-color": item.color,
			"stop-opacity": item.opacity || 1,
		})
	);
	const gradient = sv(
		"linearGradient",
		{
			id,
			gradientTransform: "rotate(90)",
		},
		colors
	);
	return gradient;
}

export function getRadialGradient(opts: any, stops: IStop[], id: string) {
	const colors = stops.map(item =>
		sv("stop", {
			offset: item.offset,
			"stop-color": item.color,
			"stop-opacity": item.opacity || 1,
		})
	);
	const gradient = sv(
		"radialGradient",
		{ id, cx: 0, cy: 0, gradientUnits: "userSpaceOnUse", ...opts },
		colors
	);
	return gradient;
}

export function euclideanDistance(x1: number, y1: number, x2: number, y2: number): number {
	return Math.sqrt((x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2));
}

export function roundToTwoNumAfterPoint(p: number): number {
	return Math.round((p * 100 + Number.EPSILON) * 100) / 100;
}

export function verticalCenteredText(text: string) {
	return sv(
		"tspan",
		{
			dy: "0.5ex",
			style: {
				pointerEvents: "none",
			},
		},
		text
	);
}

export function verticalTopText(text: string) {
	return sv(
		"tspan",
		{
			dy: "-0.5ex",
		},
		text
	);
}

export function verticalBottomText(text: string) {
	return sv(
		"tspan",
		{
			dy: "1.5ex",
		},
		text
	);
}

export function calcPointRef(pointId: string, serieId: string): string {
	return pointId + "_" + serieId;
}

export function getClassesForRotateScale(position, angle) {
	let className = "";
	const classList = [];

	if (position === "left" || position === "top") {
		classList.push("start-text", "end-text");
	} else if (position === "right" || position === "bottom") {
		classList.push("end-text", "start-text");
	}

	switch (position) {
		case "left":
		case "right":
			if (angle === 0) {
				className = classList[1];
			} else if (angle > 0) {
				if (angle === 180) {
					className = classList[0];
				} else if (angle > 180) {
					if (angle < 270) {
						className = classList[0];
					} else if (angle > 270) {
						className = classList[1];
					}
				} else if (angle < 180) {
					if (angle > 90) {
						className = classList[0];
					} else if (angle < 90) {
						className = classList[1];
					}
				}
			} else if (angle < 0) {
				if (angle === -180) {
					className = classList[0];
				} else if (angle < -180) {
					if (angle > -270) {
						className = classList[0];
					} else if (angle < -270) {
						className = classList[1];
					}
				} else if (angle > -180) {
					if (angle < -90) {
						className = classList[0];
					} else if (angle > -90) {
						className = classList[1];
					}
				}
			}
			break;
		case "top":
		case "bottom":
			if (angle > 0) {
				if (angle > 180) {
					className = classList[0];
				} else if (angle < 180) {
					className = classList[1];
				}
			} else if (angle < 0) {
				if (angle > -180) {
					className = classList[0];
				} else if (angle < -180) {
					className = classList[1];
				}
			}
			break;
	}
	return className;
}

export function getScales(config) {
	const scales = [];
	for (const scaleName in config) {
		const scale = config[scaleName];
		if (scale.min || scale.max || scale.maxTicks || scale.text || scale.value) {
			scales.push(scaleName);
		}
	}
	return scales;
}

export function getSizesSVGText(text: string, config?: IContainerConfig) {
	const sizes = [];
	config = {
		font: "normal 14px Roboto",
		lineHeight: 18,
		...config,
	};
	sizes.push(getTextWidth(text, config.font));
	sizes.push(config.lineHeight);
	return sizes;
}

export function superposition(objA: IText, objB: IText) {
	if (
		objA.x < objB.x + objB.width &&
		objA.x + objA.width > objB.x &&
		objA.y < objB.y + objB.height &&
		objA.y + objA.height > objB.y
	) {
		return true;
	} else return false;
}

export function checkPositions(
	current: IText,
	previos: IText,
	radiusX: number,
	radiusY: number,
	obj: ITextObj
) {
	if (superposition(current, previos)) {
		const dY = obj.right
			? previos.y - current.y + current.height
			: previos.y - previos.height - current.y;
		let newY = obj.text1.y + dY;
		if (Math.abs(newY) + obj.dy > radiusY) {
			newY = newY > 0 ? radiusY + obj.dy : -radiusY + obj.dy;
			obj.changeSector = !obj.changeSector;
		}
		let newX = Math.sqrt(Math.pow(radiusX, 2) - Math.pow(((newY - obj.dy) * radiusX) / radiusY, 2));
		newX = obj.right ? newX : -newX;
		if (obj.changeSector) {
			newX *= -1;
			obj.line *= -1;
			current.class = obj.right ? "pie-value end-text" : "pie-value start-text";
		}
		obj.text1.x = newX;
		obj.text1.y = newY;
		obj.text2.x = newX;
		obj.text2.y = newY + 16;
	}
}
